import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faPaperPlane,
  faSpinner,
  faTimes,
  faUser,
  faCalendarAlt,
  faFlag,
  faListCheck,
  faSlidersH,
  faAlignLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  normalizeTaskProgressPercent,
  resolveTaskProgressState,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  toDateInputValue,
} from "../taskUtils";

const BRAND_LR = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_DIAG = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const PRIORITY_COLORS = {
  Low:    { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46" },
  Medium: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
  High:   { bg: "#fff7ed", border: "#fb923c", text: "#9a3412" },
  Urgent: { bg: "#fff1f2", border: "#fda4af", text: "#9f1239" },
};

const buildDefaultValues = ({ ticket, identity, employees, initialValues }) => {
  if (initialValues) {
    return {
      title: initialValues.title || "",
      description: initialValues.description || "",
      priority: initialValues.priority || "Medium",
      status: initialValues.status || "Pending",
      progressPercent: normalizeTaskProgressPercent(initialValues.progressPercent),
      dueDate: initialValues.dueDate ? String(initialValues.dueDate).split("T")[0] : "",
      assignedEmployeeId: String(initialValues.assignedEmployeeId || ""),
    };
  }
  const selfEmployee = (employees || []).find(
    (item) => String(item.userId || "") === String(identity?.userId || "")
  );
  return {
    title: ticket?.subject ? `Follow up: ${ticket.subject}` : "",
    description: ticket?.description
      ? `Task generated from support ticket ${ticket.ticketNumber || ""}.\n\nCustomer issue:\n${ticket.description}`
      : "",
    priority: ticket?.priority || "Medium",
    status: "Pending",
    progressPercent: 0,
    dueDate: toDateInputValue(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    assignedEmployeeId: selfEmployee?.id ? String(selfEmployee.id) : "",
  };
};

export default function TaskComposerModal({
  open, ticket, employees, identity, initialValues,
  submitting, mode = "create", onClose, onSubmit,
}) {
  const [formState, setFormState] = useState(() =>
    buildDefaultValues({ ticket, identity, employees, initialValues })
  );
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setFormState(buildDefaultValues({ ticket, identity, employees, initialValues }));
    setErrors({});
  }, [open, ticket, identity, employees, initialValues]);

  const assignee = useMemo(
    () => (employees || []).find((item) => String(item.id) === String(formState.assignedEmployeeId || "")) || null,
    [employees, formState.assignedEmployeeId]
  );

  const canEditProgress =
    mode !== "edit" ||
    String(initialValues?.assignedUserId || "") === String(identity?.userId || "");

  const pct = formState.progressPercent;
  const priorityColor = PRIORITY_COLORS[formState.priority] || PRIORITY_COLORS.Medium;

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!formState.title.trim()) e.title = "Task title is required.";
    if (!formState.description.trim()) e.description = "Task details are required.";
    if (!formState.assignedEmployeeId) e.assignedEmployeeId = "Choose an employee.";
    if (!formState.dueDate) e.dueDate = "Due date is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      ...resolveTaskProgressState(formState.status, formState.progressPercent),
      title: formState.title.trim(),
      description: formState.description.trim(),
      priority: formState.priority,
      dueDate: formState.dueDate,
      assignedEmployeeId: Number(formState.assignedEmployeeId || 0),
      assignedUserId: assignee?.userId || "",
      assignedEmployeeName: assignee?.fullName || "",
      assignedEmployeeEmail: assignee?.workEmail || "",
    });
  };

  const setField = (key, value) => setFormState((cur) => ({ ...cur, [key]: value }));

  const handleProgressChange = (val) => {
    const resolved = resolveTaskProgressState(formState.status, val);
    setFormState((cur) => ({ ...cur, status: resolved.status, progressPercent: resolved.progressPercent }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-3">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal shell */}
      <div
        className="relative z-10 flex w-full flex-col overflow-hidden bg-white dark:bg-gray-900 shadow-2xl"
        style={{
          maxWidth: 600,
          maxHeight: "96dvh",
          borderRadius: "1.25rem 1.25rem 0 0",
        }}
      >
        {/* ── Header ── */}
        <div className="relative shrink-0 overflow-hidden px-4 py-4 text-white sm:px-6" style={{ background: BRAND_DIAG }}>
          <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/5 blur-2xl" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          <div className="pr-8">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
              <FontAwesomeIcon icon={faClipboardCheck} />
              {mode === "edit" ? "Update Task" : "New Task"}
            </div>
            <h3 className="mt-1.5 text-base font-extrabold tracking-tight text-white sm:text-lg">
              {mode === "edit" ? "Update task details" : "Create a new task"}
            </h3>
            <p className="mt-0.5 text-[11px] text-orange-200">
              {ticket?.ticketNumber
                ? <>Ticket: <span className="font-semibold text-white">{ticket.ticketNumber}</span></>
                : "Fill in the details below"}
            </p>
          </div>

          {/* Progress strip */}
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/15">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, background: pct === 100 ? "#6ee7b7" : "rgba(255,255,255,0.65)" }}
            />
          </div>
          <p className="mt-0.5 text-right text-[10px] text-orange-200">{pct}% complete</p>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

            {/* Title */}
            <div className="col-span-1 sm:col-span-2">
              <Field label="Task Title" icon={faListCheck} required error={errors.title}>
                <input
                  value={formState.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="e.g. Verify invoice and call customer"
                  className="fi"
                />
              </Field>
            </div>

            {/* Assign To */}
            <Field label="Assign To" icon={faUser} required error={errors.assignedEmployeeId}>
              <select value={formState.assignedEmployeeId} onChange={(e) => setField("assignedEmployeeId", e.target.value)} className="fi">
                <option value="">Select employee</option>
                {(employees || []).map((emp) => (
                  <option key={emp.id || emp.userId} value={emp.id}>
                    {emp.fullName}{emp.employeeCode ? ` (${emp.employeeCode})` : ""}
                  </option>
                ))}
              </select>
            </Field>

            {/* Due Date */}
            <Field label="Due Date" icon={faCalendarAlt} required error={errors.dueDate}>
              <input type="date" value={formState.dueDate} onChange={(e) => setField("dueDate", e.target.value)} className="fi" />
            </Field>

            {/* Priority */}
            <Field label="Priority" icon={faFlag}>
              <div className="flex flex-wrap gap-1.5">
                {TASK_PRIORITY_OPTIONS.map((p) => {
                  const c = PRIORITY_COLORS[p];
                  const active = formState.priority === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setField("priority", p)}
                      className={`rounded-lg border px-3 py-1 text-xs font-bold transition-all${active ? "" : " bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500"}`}
                      style={active ? {
                        background: c.bg,
                        borderColor: c.border,
                        color: c.text,
                        boxShadow: `0 0 0 2px ${c.border}50`,
                      } : undefined}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Status */}
            <Field label="Status" icon={faSlidersH}>
              <select
                value={formState.status}
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  const resolved = resolveTaskProgressState(nextStatus, nextStatus === "Completed" ? 100 : formState.progressPercent);
                  setFormState((cur) => ({ ...cur, status: resolved.status, progressPercent: resolved.progressPercent }));
                }}
                className="fi"
              >
                {TASK_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>

            {/* Progress */}
            <div className="col-span-1 sm:col-span-2">
              <Field label="Progress" icon={faSlidersH}>
                <div className="rounded-xl border border-slate-100 bg-slate-50 dark:border-gray-700 dark:bg-gray-800/50 px-3 py-2.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-500 dark:text-gray-400">Completion</span>
                    <span className="font-extrabold" style={{ color: pct === 100 ? "#10b981" : "#C04C2A" }}>{pct}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: pct === 100 ? "linear-gradient(to right,#34d399,#10b981)" : BRAND_LR }}
                    />
                  </div>
                  <input
                    type="range" min="0" max="100" step="5" value={pct}
                    disabled={!canEditProgress}
                    onChange={(e) => handleProgressChange(e.target.value)}
                    className={`mt-2 w-full ${!canEditProgress ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                    style={{ accentColor: "#C04C2A" }}
                  />
                  <div className="mt-1.5 flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" value={pct}
                      disabled={!canEditProgress}
                      onChange={(e) => handleProgressChange(e.target.value)}
                      className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:disabled:bg-gray-900"
                    />
                    <span className="text-[11px] text-slate-400">
                      {canEditProgress ? "100% auto-completes task." : "Only the active assignee can change progress."}
                    </span>
                  </div>
                </div>
              </Field>
            </div>

            {/* Task Details */}
            <div className="col-span-1 sm:col-span-2">
              <Field label="Task Details" icon={faAlignLeft} required error={errors.description}>
                <textarea
                  value={formState.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Add work details, customer context, expected result, and next steps."
                  rows={3}
                  className="fi resize-none"
                />
              </Field>
            </div>

            {/* Created By */}
            <div className="col-span-1 sm:col-span-2">
              <div
                className="flex items-center gap-2.5 rounded-xl border px-3 py-2"
                style={{ borderColor: "rgba(192,76,42,0.18)", background: "rgba(192,76,42,0.04)" }}
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ background: BRAND_LR }}
                >
                  {(identity?.fullName || identity?.email || "U").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: "#C04C2A" }}>Created By</div>
                  <div className="truncate text-[12px] font-semibold text-slate-800 dark:text-gray-200">{identity?.fullName || identity?.email || "Logged in user"}</div>
                  <div className="text-[10px] text-slate-400">ID: {identity?.userId || "—"}</div>
                </div>
                {/* priority badge on the right */}
                <span
                  className="ml-auto shrink-0 rounded-lg border px-2 py-0.5 text-[10px] font-bold"
                  style={{ background: priorityColor.bg, borderColor: priorityColor.border, color: priorityColor.text }}
                >
                  {formState.priority}
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 bg-slate-50 dark:border-gray-800 dark:bg-gray-900 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 sm:flex-none sm:px-5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60 sm:flex-none sm:px-6"
            style={{ background: BRAND_LR }}
          >
            <FontAwesomeIcon icon={submitting ? faSpinner : faPaperPlane} spin={submitting} />
            {submitting ? "Saving…" : mode === "edit" ? "Update Task" : "Create Task"}
          </button>
        </div>
      </div>

      <style>{`
        .fi { width:100%; border-radius:0.75rem; border:1px solid #e2e8f0; background:#fff; padding:0.5rem 0.875rem; font-size:0.8125rem; color:#334155; outline:none; transition:border-color 0.15s,box-shadow 0.15s; }
        .fi:focus { border-color:#C04C2A; box-shadow:0 0 0 3px rgba(192,76,42,0.10); }
        .fi:disabled { background:#f8fafc; color:#94a3b8; cursor:not-allowed; }
        .dark .fi { background:#1f2937; border-color:#374151; color:#e5e7eb; }
        .dark .fi:focus { border-color:#C04C2A; }
        .dark .fi:disabled { background:#111827; color:#6b7280; }
      `}</style>
    </div>
  );
}

function Field({ label, icon, required, error, children }) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {icon ? <FontAwesomeIcon icon={icon} className="text-[9px]" style={{ color: "#C04C2A" }} /> : null}
        {label}
        {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? (
        <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-rose-600">
          <span className="h-1 w-1 shrink-0 rounded-full bg-rose-500" />
          {error}
        </div>
      ) : null}
    </div>
  );
}
